import { DateTime } from 'luxon'
import React, { Fragment, FunctionComponent, useEffect } from 'react'
import { Col, Row } from 'react-grid-system'
import { useTranslation } from 'react-i18next'

import {
  Column,
  HeaderText,
  InformationText,
  StyledBookmarkButton,
  StyledDeliveryCard,
  StyledHeaderColumn,
  StyledHeaderTitle,
  StyledHeaderTitleContainer,
  StyledLicenseBox,
  StyledProductDetail,
  StyledProductInformation,
  StyledReturnButton,
  StyledRow,
  StyledSKUQuantitySelector,
  StyledStrengthText,
  StyledClinPatientTableHeaderCell,
  StyledClinTable,
  FixedWidthCell,
  StyledClinTableBodyCell,
  StyledClinTableRow,
  StyledDocumentsTableContainer,
  StyledDocumentsTableTitle,
  StyledDocumentActions,
  StyledStrengthCell,
  StyledEstimatedDeliveryDate,
  StyledButtonLabel,
  StyledClinTableRowMobile,
  ColumnMobile,
  StyledHeaderTitleMobileContainer,
  InformationRow,
  StyledRowMobile,
  StyledClinTableBodyCellMobile
} from './ProductDetail.styles'
import { ProductDetailContact } from './ProductDetailContact'
import {
  ProductDocumentUiState,
  RequestSubmissionState
} from './ProductDetailContainer'
import { ClinTheme } from '../../../ClinTheme'
import { ClinButton } from '../../../components/ClinButton'
import { getBrowserLocale } from '../../../utils/getBrowserLocale'
import { numberToCurrencyString } from '../../../utils/numberToCurrencyString'
import {
  StyledHeader,
  StyledHeaderContainer
} from '../ProductVariant/ProductVariant.styles'
import { ClinPageContentFrame } from '../../../components/ClinPageContentFrame'
import {
  BookmarkableItem,
  BookmarkableItemType
} from '../../../types/BookmarkableItem'
import { StyledSpinnerContainer } from '../../../components/ClinSpinner/ClinSpinner.styles'
import {
  IAllHoldsInformation,
  IHoldDetails
} from '../../../services/Holds/holds.types'
import { HoldType } from '../../../services/Holds/holds.constants'
import { ClinGroup } from '../../../components/ClinGroup'
import {
  IAugmentedCatalogItemDto,
  UIVariationState
} from '../../../services/PortfolioJourneys/PortfolioJourney.types'

import 'react-tippy/dist/tippy.css'
import { ClinIcon } from '../../../components/ClinIcon'
import { ClinIconPathName } from '../../../components/ClinIcon/ClinIcon.paths'
import { ClinSpacer } from '../../../components/ClinSpacer'
import { ClinSpinner } from '../../../components/ClinSpinner'
import { ClinText } from '../../../components/ClinText'
import { TypographyVariant } from '../../../components/ClinText/ClinText.styles'
import { ClinTextInput } from '../../../components/ClinTextInput'
import { isAusGaUser, isAusMaUser, UserRole } from '../../../constants'
import { CountryAlphaCodes } from '../../../constants/countryAlpha2Codes'
import {
  shouldDisplaAvailability,
  shouldDisplayEstimatedDeliveryDateProductPage
} from '../../../features/VariantCard/VariantCard.model'
import { useCheckUnableHomePage } from '../../../hooks/useCheckUnableHomePage/useCheckUnableHomePage'
import {
  CatalogDto,
  DistributorDto,
  ItemDocumentDto,
  PriceAndAvailabilityDto,
  UploadedDocumentDto
} from '../../../types/swaggerTypes'
import { BookmarkToggleButtonContainer } from '../../../features/BookmarkToggleButton/BookmarkToggleButtonContainer'
import useChangeBackgroundColor from '../../../hooks/useChangeBackgroundColor/useChangeBackgroundColor'
import { EstimatedDeliveryDate } from './EstimatedDeliveryDate/EstimatedDeliveryDate'
import StatusColumn from '../ProductVariant/StatusColumn/StatusColumn'
import Storage from './Storage/Storage'
import {
  CatalogueItemConstants,
  getCatalogueItemStatus,
  getSummarySectionHeaderTranslation
} from '../ProductVariant/ProductVariant.models'
import { StyledLabel } from '../ProductVariant/Basket/Basket.styles'

interface IProductDetailProps {
  /** Whether we are loading or not */
  isLoading: boolean
  /** Whether we are loading the unit price info or not */
  isLoadingPrice: boolean
  /** Whether we are submitting a register interest request */
  registerInterestSubmissionState: RequestSubmissionState
  /** Adding an item to basket */
  isAddingToBasket: boolean
  /** Loading drawer after updating basket */
  isDrawerLoading: boolean
  /** Allows the view to determine whether to display a sku */
  isSkuOnly: boolean
  /** Selected product */
  product?: CatalogDto // TODO: Refactor to catalog in order to match data name rather than UI name
  /** Selected catalogueItem */
  catalogueItem?: IAugmentedCatalogItemDto
  /** Minimum amount that can be purchased */
  quantity?: number | 0
  minQuantity?: number | null
  /** Maximum amount that can be purchased */
  maxQuantity?: number | null
  /** Incremental size */
  stepSize?: number | null
  /** Errors with min max quantity */
  quantityError?: string | null
  /** Currency code for this product */
  currencyCode: string | undefined
  /** Total cost */
  totalCost?: number
  /** Unit price */
  unitPrice: number
  /** All the holds information for this product */
  holdsInformation?: IAllHoldsInformation
  /** Link to sourcing request form with URL params */
  sourcingRequestURL?: string
  /** Distributor information */
  distributor?: DistributorDto
  /** Multiple currencies are in the basket or basket contains different currency to product */
  hasCurrencyError?: boolean
  /** Documents to be displayed to the user **/
  productDocuments?: ProductDocumentUiState[]
  /** Whether this is a TSE Expired or Restricted product (for debugging) */
  isRestricted?: boolean
  /** Price And Availability information */
  priceAndAvailability?: PriceAndAvailabilityDto
  /** Handle back button click */
  handleGoBack?: (event: React.MouseEvent<HTMLButtonElement>) => void
  /** Handle document download */
  handleDownload: (documentItem: ItemDocumentDto) => void
  /** Handle changing quantity */
  handleChangeQuantity?: (quantity: number) => void
  /** Handle add to basket */
  handleAddToBasket?: (event: React.MouseEvent<HTMLButtonElement>) => void
  /** Request to print order **/
  handlePrint?: () => void
  /** Render prop for showing bookmarks */
  renderBookmarkToggle?: FunctionComponent<BookmarkableItem>
  /** Handle document association with sku and hold for uploads */
  handleFileUploadForSku?: (
    sku: string,
    holdDetails: IHoldDetails,
    document: UploadedDocumentDto
  ) => void
  /** Register interest for GA **/
  handleRegisterInterestForProduct?: () => void
  /** Request skinny sourcing **/
  handleSkinnySourcingRequest?: () => void
  /** Request skinny sourcing flag **/
  skinnySourcingButtonDisabled?: boolean
  /** User country*/
  userCountry: string
  /** User role*/
  userRole?: UserRole
}

export const ProductDetail: FunctionComponent<IProductDetailProps> = ({
  isLoading,
  isLoadingPrice,
  registerInterestSubmissionState,
  isAddingToBasket,
  isDrawerLoading,
  product,
  isSkuOnly,
  catalogueItem,
  quantity = 0,
  minQuantity = 1,
  maxQuantity = 10,
  stepSize = 1,
  quantityError,
  totalCost = 0,
  currencyCode,
  unitPrice,
  holdsInformation,
  sourcingRequestURL,
  distributor,
  hasCurrencyError,
  userCountry,
  userRole,
  productDocuments,
  priceAndAvailability,
  handleGoBack,
  handleChangeQuantity,
  handleAddToBasket,
  handleRegisterInterestForProduct,
  handleSkinnySourcingRequest,
  handleDownload,
  skinnySourcingButtonDisabled = false
}) => {
  const { t } = useTranslation()
  const { enableNewHomePage } = useCheckUnableHomePage()
  useChangeBackgroundColor(ClinTheme.colors.lightGrey)
  const [isMobile, setIsMobile] = React.useState<boolean>(false)
  const TableRowComponent = isMobile
    ? StyledClinTableRowMobile
    : StyledClinTableRow

  const ColumnComponent = isMobile ? ColumnMobile : Column

  const ColumnHeaderTitleComponent = isMobile
    ? StyledHeaderTitleMobileContainer
    : StyledHeaderTitleContainer

  const updateIsMobile = () => {
    setIsMobile(
      (window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth) <= ClinTheme.breakpoints[2]
    )
  }

  const listener = () => {
    updateIsMobile()
  }

  useEffect(() => {
    updateIsMobile() // Set initial state
    window.addEventListener('resize', listener)
    return () => {
      window.removeEventListener('resize', listener)
    }
  }, [])

  const catalogueItemStatus =
    catalogueItem &&
    getCatalogueItemStatus(catalogueItem, userCountry, priceAndAvailability)

  const shouldDisplayEstimatedDeliveryDate = (
    catalogueItemStatus: string,
    catalogueItem: IAugmentedCatalogItemDto
  ): boolean => {
    return (
      ![
        CatalogueItemConstants.RequestQuote,
        CatalogueItemConstants.BackOrder,
        CatalogueItemConstants.OutOfStock
      ].includes(catalogueItemStatus as CatalogueItemConstants) &&
      shouldDisplayEstimatedDeliveryDateProductPage(
        catalogueItem.expectedDate,
        catalogueItem,
        catalogueItem.cutOffTime
      )
    )
  }

  const getCrumbs = () => {
    const crumbsArray = !isSkuOnly
      ? [
          isAusGaUser(userCountry, userRole) && enableNewHomePage
            ? {
                name: t('navigation:home'),
                path: '/home'
              }
            : { path: '/orders', name: t('orders:page_name') },
          {
            name: t('product_catalogue:non_ma_title'),
            path: '/products/catalogue'
          },
          {
            name: product ? product.catalogItemName : '',
            path: `/product/${product && product.catalogDocumentId}`
          },
          {
            name: catalogueItem ? catalogueItem.sku : '',
            path: `/product/${product && product.catalogDocumentId}/variant/${
              catalogueItem?.sku
            }`
          }
        ]
      : [
          isAusGaUser(userCountry, userRole) && enableNewHomePage
            ? {
                name: t('navigation:home'),
                path: '/home'
              }
            : { path: '/orders', name: t('orders:page_name') },
          {
            name: t('product_catalogue:non_ma_title'),
            path: '/products/catalogue'
          },
          {
            name: product ? product.catalogItemName : '',
            path: `/product/${product && product.catalogDocumentId}`
          }
        ]

    return crumbsArray
  }
  const getCatalogueItemColumns = () => [
    {
      name: 'documentName',
      viewName: 'Document Name',
      translationKey: '',
      width: '100%',
      minWidth: '75px',
      maxWidth: '125px',
      isSortable: false,
      renderValue: (documentItem: ProductDocumentUiState) => (
        <>
          <StyledStrengthCell
            key={`document-${documentItem.document.generatedId}`}
          >
            <ClinText
              variant={TypographyVariant.SmallUI}
              lineHeight={ClinTheme.lineHeights.heading[0]}
              fontWeight={
                isMobile
                  ? ClinTheme.fontWeights.normal
                  : ClinTheme.fontWeights.bold
              }
              fontSize={ClinTheme.fontSizes[1]}
            >
              {documentItem.document.documentType ?? '--'}
            </ClinText>
          </StyledStrengthCell>
        </>
      )
    },
    {
      name: 'language',
      viewName: 'Language',
      translationKey: '',
      minWidth: '75px',
      maxWidth: '125px',
      isSortable: false,
      renderValue: (documentItem: ProductDocumentUiState) => (
        <ClinText
          fontSize={ClinTheme.fontSizes[1]}
          lineHeight={ClinTheme.lineHeights.heading[0]}
        >
          {documentItem.document.documentLanguage ?? '--'}
        </ClinText>
      )
    },
    {
      name: 'lastUpdated',
      viewName: 'Last Revised',
      translationKey: '',
      minWidth: '75px',
      maxWidth: '125px',
      isSortable: false,
      renderValue: (documentItem: ProductDocumentUiState) => (
        <ClinText
          fontSize={ClinTheme.fontSizes[1]}
          lineHeight={ClinTheme.lineHeights.heading[0]}
        >
          {documentItem.document.revisionDate
            ? DateTime.fromISO(documentItem.document.revisionDate)
                .toFormat('dd/MMM/yyyy')
                .toUpperCase()
            : '--'}
        </ClinText>
      )
    },
    {
      name: 'actions',
      viewName: 'Actions',
      translationKey: 'product_variants:column_titles.estimated_delivery',
      minWidth: '150px',
      maxWidth: '300px',
      isSortable: false,
      renderValue: (documentItem: ProductDocumentUiState) => (
        <ClinButton
          variant="ghost"
          onClick={() =>
            documentItem.document && handleDownload(documentItem.document)
          }
          style={{ padding: '10px' }}
          disabled={documentItem.isDownloading}
        >
          {documentItem.isDownloading ? (
            <ClinSpinner size={ClinTheme.space[4]} />
          ) : (
            <ClinIcon
              iconSize={ClinTheme.space[4]}
              iconName={ClinIconPathName.Download}
              iconFill={isMobile ? ClinTheme.colors.primary : 'currentcolor'}
            />
          )}
        </ClinButton>
      )
    }
  ]
  const columns = getCatalogueItemColumns()

  return isLoading || !product ? (
    <ClinPageContentFrame>
      <Row align="center">
        <Col>
          <StyledSpinnerContainer>
            <ClinSpinner size={ClinTheme.space[7]} />
          </StyledSpinnerContainer>
        </Col>
      </Row>
    </ClinPageContentFrame>
  ) : (
    <>
      <StyledHeader>
        <ClinPageContentFrame marginBottom="0">
          <StyledHeaderContainer
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: isMobile ? 'wrap' : 'nowrap',
              minHeight: ClinTheme.componentsHeights.header,
              width: '100%',
              overflow: 'hidden'
            }}
          >
            {isMobile ? (
              <>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    flexShrink: 0,
                    gap: ClinTheme.space[2],
                    padding: `${ClinTheme.space[3]}px 20px`
                  }}
                >
                  <ClinButton
                    variant="linkWithIcon"
                    onClick={handleGoBack}
                    style={{ flexShrink: 0 }}
                  >
                    <ClinIcon iconName={ClinIconPathName.ArrowLeft} />
                  </ClinButton>

                  <ColumnHeaderTitleComponent
                    style={{
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden'
                    }}
                  >
                    <StyledHeaderTitle
                      as="h1"
                      variant={TypographyVariant.H2}
                      fontWeight={ClinTheme.fontWeights.bold}
                      color={ClinTheme.colors.black}
                      style={{
                        display: 'inline-block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontSize: ClinTheme.fontSizes[4],
                        width: '100%'
                      }}
                    >
                      {product.catalogItemName}
                      {catalogueItem?.products?.map((prod) => (
                        <StyledStrengthText
                          key={prod.strength}
                          style={{
                            fontSize: ClinTheme.fontSizes[4],
                            display: 'inline'
                          }}
                        >
                          {` / ${prod.strength}`}
                        </StyledStrengthText>
                      ))}
                    </StyledHeaderTitle>
                  </ColumnHeaderTitleComponent>

                  <StyledBookmarkButton
                    style={{
                      flexShrink: 0,
                      marginLeft: 'auto',
                      paddingRight: '20px'
                    }}
                  >
                    {catalogueItem && (
                      <BookmarkToggleButtonContainer
                        iconOnly={isMobile}
                        itemType={BookmarkableItemType.SKU}
                        itemId={catalogueItem.sku}
                        iconFirst={true}
                      />
                    )}
                  </StyledBookmarkButton>
                </div>

                {catalogueItem &&
                  catalogueItemStatus &&
                  shouldDisplayEstimatedDeliveryDate(
                    catalogueItemStatus,
                    catalogueItem
                  ) && (
                    <StyledEstimatedDeliveryDate>
                      <StyledDeliveryCard
                        style={{
                          width: isMobile ? '100%' : 'fit-content',
                          boxSizing: 'border-box',
                          flexShrink: 0,
                          marginBottom: `${ClinTheme.space[3]}px`
                        }}
                      >
                        <EstimatedDeliveryDate
                          expectedDeliveryDate={catalogueItem.expectedDate}
                          cutOffTime={catalogueItem.cutOffTime}
                        />
                      </StyledDeliveryCard>
                    </StyledEstimatedDeliveryDate>
                  )}
              </>
            ) : (
              <>
                <StyledHeaderColumn style={{ flex: 1, minWidth: 0 }}>
                  <StyledReturnButton style={{ flexShrink: 0 }}>
                    <ClinButton variant="linkWithIcon" onClick={handleGoBack}>
                      <ClinIcon iconName={ClinIconPathName.ArrowLeft} />
                      &nbsp;{t('product_detail:variant_options')}
                    </ClinButton>
                  </StyledReturnButton>

                  <ColumnHeaderTitleComponent style={{ flex: 1 }}>
                    <StyledHeaderTitle
                      as="h1"
                      variant={TypographyVariant.H2}
                      fontWeight={ClinTheme.fontWeights.bold}
                      color={ClinTheme.colors.black}
                      style={{
                        display: 'inline-block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%'
                      }}
                    >
                      {product.catalogItemName}
                      {catalogueItem?.products?.map((prod) => (
                        <StyledStrengthText key={prod.strength}>
                          {` / ${prod.strength}`}
                        </StyledStrengthText>
                      ))}
                    </StyledHeaderTitle>
                  </ColumnHeaderTitleComponent>
                </StyledHeaderColumn>

                <StyledBookmarkButton style={{ flexShrink: 0 }}>
                  {catalogueItem && (
                    <BookmarkToggleButtonContainer
                      iconOnly={isMobile}
                      itemType={BookmarkableItemType.SKU}
                      itemId={catalogueItem.sku}
                      iconFirst={true}
                    />
                  )}
                </StyledBookmarkButton>

                {catalogueItem &&
                  catalogueItemStatus &&
                  shouldDisplayEstimatedDeliveryDate(
                    catalogueItemStatus,
                    catalogueItem
                  ) && (
                    <StyledHeaderColumn style={{ flexShrink: 0 }}>
                      <StyledDeliveryCard>
                        <EstimatedDeliveryDate
                          expectedDeliveryDate={catalogueItem.expectedDate}
                          cutOffTime={catalogueItem.cutOffTime}
                        />
                      </StyledDeliveryCard>
                    </StyledHeaderColumn>
                  )}
              </>
            )}
          </StyledHeaderContainer>
        </ClinPageContentFrame>
      </StyledHeader>
      <ClinPageContentFrame crumbs={getCrumbs()}>
        <StyledProductDetail>
          {/* Display currently unavailable */}
          {catalogueItem &&
          catalogueItem?.uiVariantState ===
            UIVariationState.GloballyUnavailable ? (
            <StyledHeader>
              <ClinPageContentFrame marginBottom="0">
                <StyledHeaderContainer>
                  <StyledHeaderColumn>
                    <StyledHeaderTitle
                      as="h1"
                      variant={TypographyVariant.H2}
                      fontWeight={ClinTheme.fontWeights.bold}
                      color={ClinTheme.colors.black}
                    >
                      {product.catalogItemName}
                    </StyledHeaderTitle>
                  </StyledHeaderColumn>
                  <StyledHeaderColumn>
                    <BookmarkToggleButtonContainer
                      itemType={BookmarkableItemType.Generic}
                      itemId={catalogueItem.sku}
                      iconFirst={true}
                    />
                  </StyledHeaderColumn>
                </StyledHeaderContainer>
              </ClinPageContentFrame>
            </StyledHeader>
          ) : (
            <>
              <Row>
                <Col sm={12}>
                  {isLoadingPrice || !catalogueItem ? (
                    <Col style={{ display: 'flex', justifyContent: 'center' }}>
                      <ClinSpinner />
                    </Col>
                  ) : (
                    <StyledRowMobile
                      style={{
                        display: isMobile ? 'grid' : 'flex',
                        justifyContent: 'normal'
                      }}
                    >
                      <Col
                        style={{
                          width: '100%',
                          order: 1
                        }}
                        className="info-block"
                      >
                        <StyledProductInformation>
                          <StyledRow>
                            <ColumnComponent>
                              <HeaderText
                                style={{
                                  fontSize: 14,
                                  fontWeight: 700,
                                  color: 'black'
                                }}
                              >
                                {t('product_variants:product_information')}
                              </HeaderText>
                              <ClinSpacer />
                              <InformationRow>
                                <HeaderText>{t('glossary:brand')}</HeaderText>
                                <InformationText>
                                  {catalogueItem?.item?.brand ?? '--'}
                                </InformationText>
                              </InformationRow>
                              <InformationRow>
                                <HeaderText>
                                  {t('glossary:dosage_and_packsize')}
                                </HeaderText>
                                <InformationText>
                                  {catalogueItem?.item.packSize ?? '--'}
                                </InformationText>
                              </InformationRow>
                              <InformationRow>
                                <HeaderText>
                                  {t('glossary:product_code')}
                                </HeaderText>
                                <InformationText>
                                  {catalogueItem?.item.sku || '--'}
                                </InformationText>
                              </InformationRow>
                              <InformationRow>
                                <HeaderText>
                                  {t('glossary:overlabelled')}
                                </HeaderText>
                                <InformationText>
                                  {catalogueItem?.item?.overlabelled === 'Y'
                                    ? `${catalogueItem.item.overlabelLanguage} (${catalogueItem.item.overlabelType})`
                                    : t('glossary:no')}
                                </InformationText>
                              </InformationRow>

                              <InformationRow>
                                <HeaderText>
                                  {t('glossary:manufacturer')}
                                </HeaderText>
                                <InformationText>
                                  {catalogueItem?.item?.manufacturer ?? '--'}
                                </InformationText>
                              </InformationRow>
                            </ColumnComponent>

                            <ColumnComponent>
                              <HeaderText
                                style={{
                                  fontSize: 14,
                                  fontWeight: 700,
                                  color: 'black'
                                }}
                              >
                                {t('glossary:licensing')}
                              </HeaderText>
                              <ClinSpacer />
                              <InformationRow>
                                <HeaderText>
                                  {t('glossary:country_of_license')}
                                </HeaderText>
                                <InformationText>
                                  {catalogueItem?.licenses?.length
                                    ? catalogueItem.licenses
                                        .map((license) => license.country)
                                        .join(', ')
                                    : '--'}
                                </InformationText>
                              </InformationRow>
                              <InformationRow>
                                <HeaderText>
                                  {t('glossary:license_holder')}
                                </HeaderText>
                                <InformationText>
                                  {catalogueItem?.item?.maHolder ?? '--'}
                                </InformationText>
                              </InformationRow>
                              <InformationRow>
                                <HeaderText>
                                  {t('glossary:license_number')}
                                </HeaderText>
                                <InformationText>
                                  {catalogueItem?.licenses?.length
                                    ? catalogueItem.licenses.map(
                                        (license) => license.licenseNumber
                                      )
                                    : '--'}
                                </InformationText>
                              </InformationRow>
                            </ColumnComponent>

                            <ColumnComponent>
                              <HeaderText
                                style={{
                                  fontSize: 14,
                                  fontWeight: 700,
                                  color: 'black'
                                }}
                              >
                                {t('glossary:handling_and_storage')}
                              </HeaderText>
                              <ClinSpacer />
                              <InformationRow>
                                <HeaderText>
                                  {t('glossary:controlled_drug_status')}
                                </HeaderText>
                                <InformationText>
                                  {catalogueItem?.item?.controlledDrug ?? '--'}
                                  <StyledLicenseBox>
                                    {userCountry !==
                                      CountryAlphaCodes.Australia &&
                                      holdsInformation?.holdsDetails?.some(
                                        (hold) =>
                                          hold.type === HoldType.ControlledDrug
                                      ) && (
                                        <StyledLabel>
                                          <ClinText
                                            whiteSpace="nowrap"
                                            color={
                                              ClinTheme.colors.primaryLight
                                            }
                                            fontWeight={
                                              ClinTheme.fontWeights.medium
                                            }
                                            variant={TypographyVariant.SmallUI}
                                            lineHeight={
                                              ClinTheme.lineHeights
                                                .largeParagraph
                                            }
                                            textAlign="center"
                                            display="flex"
                                          >
                                            {t(
                                              'product_variants:import_license_required'
                                            )}
                                          </ClinText>
                                        </StyledLabel>
                                      )}
                                  </StyledLicenseBox>
                                </InformationText>
                              </InformationRow>
                              <InformationRow>
                                <HeaderText>
                                  {t('glossary:expiry_date')}
                                </HeaderText>
                                <InformationText>
                                  {t('product_detail:greater_than_six_months')}
                                </InformationText>
                              </InformationRow>
                              <InformationRow>
                                <HeaderText>
                                  {t('glossary:cytotoxicity')}
                                </HeaderText>
                                <InformationText>
                                  {catalogueItem?.item?.cytotoxic === 'Y'
                                    ? t('glossary:yes')
                                    : t('glossary:no')}
                                </InformationText>
                              </InformationRow>
                              <InformationRow>
                                <HeaderText>{t('glossary:storage')}</HeaderText>
                                <InformationText>
                                  {catalogueItem?.item?.storageTemperature ? (
                                    <Storage
                                      storage={
                                        catalogueItem.item.storageTemperature
                                      }
                                    />
                                  ) : (
                                    '--'
                                  )}
                                </InformationText>
                              </InformationRow>
                            </ColumnComponent>
                          </StyledRow>
                        </StyledProductInformation>
                        {productDocuments && productDocuments.length > 0 && (
                          <StyledDocumentsTableContainer>
                            <StyledDocumentsTableTitle
                              fontSize={ClinTheme.fontSizes[1]}
                              fontWeight={ClinTheme.fontWeights.bold}
                              lineHeight={ClinTheme.lineHeights.heading[1]}
                            >
                              {'Product documents'}
                            </StyledDocumentsTableTitle>
                            <StyledClinTable
                              borderColapseSeparate={true}
                              tableHeader={() =>
                                isMobile ? (
                                  <>
                                    <ClinSpacer />
                                  </>
                                ) : (
                                  columns.map((column) => (
                                    <StyledClinPatientTableHeaderCell
                                      key={`th-${column.name}`}
                                      background={ClinTheme.colors.lightGrey}
                                    >
                                      <FixedWidthCell
                                        width={column.width}
                                        minWidth={column.minWidth}
                                        maxWidth={column.maxWidth}
                                      >
                                        {column.viewName === 'Actions' ? (
                                          <StyledDocumentActions>
                                            {column.viewName}
                                          </StyledDocumentActions>
                                        ) : (
                                          column.viewName
                                        )}
                                      </FixedWidthCell>
                                    </StyledClinPatientTableHeaderCell>
                                  ))
                                )
                              }
                            >
                              {productDocuments &&
                                productDocuments
                                  .filter(
                                    (item: ProductDocumentUiState) =>
                                      item.document.externallyVisible === 'Y'
                                  )
                                  .map((documentItem, index) => {
                                    return (
                                      <Fragment
                                        key={`document-row-${documentItem.document.generatedId}`}
                                      >
                                        {/* Main Row */}
                                        <TableRowComponent
                                          background={ClinTheme.colors.white}
                                        >
                                          {isMobile && (
                                            <StyledClinTableBodyCellMobile
                                              style={{
                                                padding: ClinTheme.space[3],
                                                gridColumn: '1 / -1'
                                              }}
                                            >
                                              <ClinText
                                                fontSize={14}
                                                fontWeight={500}
                                              >
                                                {
                                                  documentItem.document
                                                    .documentType
                                                }
                                              </ClinText>
                                            </StyledClinTableBodyCellMobile>
                                          )}
                                          {columns?.map((column) => (
                                            <>
                                              {isMobile && (
                                                <StyledClinTableBodyCellMobile
                                                  key={`label-${column.name}`}
                                                >
                                                  <FixedWidthCell
                                                    width={column.width}
                                                    minWidth={column.minWidth}
                                                    maxWidth={column.maxWidth}
                                                  >
                                                    {column.viewName ===
                                                    'Actions'
                                                      ? null
                                                      : column.viewName}
                                                  </FixedWidthCell>
                                                </StyledClinTableBodyCellMobile>
                                              )}

                                              {isMobile ? (
                                                <StyledClinTableBodyCellMobile
                                                  key={`${column.name}-${documentItem.document.generatedId}`}
                                                >
                                                  <FixedWidthCell
                                                    width={column.width}
                                                    minWidth={column.minWidth}
                                                    maxWidth={''}
                                                    style={{
                                                      paddingRight: '6px',
                                                      display:
                                                        column.viewName ===
                                                        'Actions'
                                                          ? ''
                                                          : 'flex',

                                                      justifyContent:
                                                        column.viewName ===
                                                        'Actions'
                                                          ? 'flex-end'
                                                          : 'flex-start',
                                                      textAlign:
                                                        column.viewName ===
                                                        'Actions'
                                                          ? 'right'
                                                          : 'left'
                                                    }}
                                                  >
                                                    {column.renderValue
                                                      ? column.renderValue(
                                                          documentItem
                                                        )
                                                      : null}
                                                  </FixedWidthCell>
                                                </StyledClinTableBodyCellMobile>
                                              ) : (
                                                <StyledClinTableBodyCell
                                                  key={`${column.name}-${documentItem.document.generatedId}`}
                                                >
                                                  <FixedWidthCell
                                                    width={column.width}
                                                    minWidth={column.minWidth}
                                                    maxWidth={column.maxWidth}
                                                  >
                                                    {column.renderValue
                                                      ? column.renderValue(
                                                          documentItem
                                                        )
                                                      : null}
                                                  </FixedWidthCell>
                                                </StyledClinTableBodyCell>
                                              )}
                                            </>
                                          ))}
                                        </TableRowComponent>
                                        {index !==
                                          productDocuments.length - 1 && (
                                          <ClinSpacer
                                            height={ClinTheme.space[2]}
                                          />
                                        )}
                                      </Fragment>
                                    )
                                  })}
                            </StyledClinTable>
                          </StyledDocumentsTableContainer>
                        )}
                      </Col>
                      {/* SKU Selector */}
                      {isLoadingPrice ? (
                        <Col
                          style={{ display: 'flex', justifyContent: 'center' }}
                        >
                          <ClinSpinner />
                        </Col>
                      ) : (
                        <Col
                          style={{
                            paddingLeft: '0',
                            paddingRight: '0',
                            marginRight: ClinTheme.space[3],
                            maxWidth: isMobile ? '100%' : 268,
                            order: 2
                          }}
                          className="quote-block"
                        >
                          <>
                            <StyledSKUQuantitySelector
                              className={'sku-quantity-selector'}
                              style={{
                                background: ClinTheme.colors.white,
                                border: `2px solid ${ClinTheme.colors.black}`,
                                margin: '0 16px',
                                borderRadius: '8px'
                              }}
                            >
                              {/* we don't want to display availability for aus user where product division is sp - CLOS-2750 */}
                              {catalogueItem &&
                                shouldDisplaAvailability(
                                  catalogueItem!,
                                  userCountry
                                ) && (
                                  <StatusColumn
                                    availableQuantity={
                                      priceAndAvailability?.availableQuantity
                                    }
                                    catalogueItemStatus={catalogueItemStatus!}
                                  />
                                )}
                              {catalogueItem &&
                                !catalogueItem.showDistributorInfo && (
                                  <ClinText
                                    as="div"
                                    fontSize={ClinTheme.fontSizes[3]}
                                    fontWeight={ClinTheme.fontWeights.medium}
                                    lineHeight="24px"
                                    marginTop={ClinTheme.space[4]}
                                    marginBottom={ClinTheme.space[4]}
                                  >
                                    {getSummarySectionHeaderTranslation(
                                      Number(
                                        priceAndAvailability?.availableQuantity
                                      ) > 0
                                        ? CatalogueItemConstants.Available
                                        : catalogueItemStatus!
                                    )}
                                  </ClinText>
                                )}

                              {/*<PortfolioJourneyDebug*/}
                              {/*  showDebug={false}*/}
                              {/*  catalogueItem={catalogueItem}*/}
                              {/*  showSKU={false}*/}
                              {/*/>*/}
                              {/* Register Interest (portfolio journeys 10, 11, 12) */}
                              {catalogueItem &&
                                sourcingRequestURL &&
                                catalogueItem.canRegisterInterest && (
                                  <>
                                    <ClinText
                                      as="div"
                                      variant={TypographyVariant.Paragraph}
                                      fontWeight={ClinTheme.fontWeights.medium}
                                      marginBottom={ClinTheme.space[3]}
                                    >
                                      {t(
                                        'product_detail:not_available_for_sale_in_your_country'
                                      )}
                                    </ClinText>
                                    <ClinText
                                      as="div"
                                      variant={TypographyVariant.Paragraph}
                                      marginBottom={ClinTheme.space[3]}
                                    >
                                      {t(
                                        'product_detail:please_register_your_interest'
                                      )}
                                    </ClinText>
                                    <ClinButton
                                      variant="primary"
                                      display="block"
                                      onClick={handleRegisterInterestForProduct}
                                      disabled={
                                        registerInterestSubmissionState ===
                                          RequestSubmissionState.Submitted ||
                                        registerInterestSubmissionState ===
                                          RequestSubmissionState.Submitting
                                      }
                                    >
                                      {registerInterestSubmissionState ===
                                      RequestSubmissionState.NotSubmitted ? (
                                        t('product_detail:register_interest')
                                      ) : (
                                        <>
                                          {registerInterestSubmissionState ===
                                          RequestSubmissionState.Submitting ? (
                                            <ClinGroup
                                              alignItems="center"
                                              justifyContent="space-between"
                                            >
                                              {registerInterestSubmissionState ===
                                              RequestSubmissionState.Submitting
                                                ? t(
                                                    'product_detail:registering'
                                                  )
                                                : t(
                                                    'product_detail:registered'
                                                  )}
                                              {registerInterestSubmissionState ===
                                                RequestSubmissionState.Submitting && (
                                                <div
                                                  style={{
                                                    marginLeft: 'auto',
                                                    marginTop:
                                                      ClinTheme.space[1]
                                                  }}
                                                >
                                                  <ClinSpinner
                                                    size={ClinTheme.space[3]}
                                                  />
                                                </div>
                                              )}
                                            </ClinGroup>
                                          ) : (
                                            t('product_detail:registered')
                                          )}
                                        </>
                                      )}
                                    </ClinButton>
                                  </>
                                )}

                              {/* Request Product (portfolio journeys 7, 14, 15, 6) */}
                              {catalogueItem &&
                                sourcingRequestURL &&
                                catalogueItem.canAccessSourcingRequestForm && (
                                  <>
                                    {catalogueItem.uiVariantState !==
                                      UIVariationState.AvailableOnRequestOnRequest && (
                                      <>
                                        <ClinText
                                          as="div"
                                          variant={TypographyVariant.Paragraph}
                                          fontWeight={
                                            ClinTheme.fontWeights.medium
                                          }
                                          marginBottom={ClinTheme.space[3]}
                                        >
                                          {t(
                                            'product_detail:not_available_for_sale'
                                          )}
                                        </ClinText>
                                        <ClinText
                                          as="div"
                                          variant={TypographyVariant.Paragraph}
                                          marginBottom={ClinTheme.space[3]}
                                        >
                                          {t(
                                            'product_detail:please_register_your_interest'
                                          )}
                                        </ClinText>
                                      </>
                                    )}

                                    <ClinTextInput
                                      name="quantity"
                                      id="quantity"
                                      label={t(
                                        'product_variants:enter_quantity'
                                      )}
                                      type="number"
                                      autoFocus={!hasCurrencyError}
                                      disabled={skinnySourcingButtonDisabled}
                                      hasError={!!quantityError}
                                      prompt={
                                        quantityError ? quantityError : ''
                                      }
                                      step={stepSize || undefined}
                                      min={minQuantity || undefined}
                                      max={maxQuantity || 10000}
                                      width={'100%'}
                                      onChange={(event) =>
                                        handleChangeQuantity &&
                                        handleChangeQuantity(
                                          Number.parseInt(
                                            event.currentTarget.value
                                          )
                                        )
                                      }
                                    />
                                    <ClinSpacer height={ClinTheme.space[5]} />
                                    <ClinButton
                                      variant="primary"
                                      display="block"
                                      disabled={
                                        skinnySourcingButtonDisabled ||
                                        !!quantityError
                                      }
                                      onClick={handleSkinnySourcingRequest}
                                    >
                                      {skinnySourcingButtonDisabled
                                        ? t('product_detail:requested_product')
                                        : t('product_detail:request_product')}
                                    </ClinButton>
                                  </>
                                )}

                              {/* Out of Stock button - only show if canRegisterInterest is false */}
                              {catalogueItem &&
                                !catalogueItem.canRegisterInterest &&
                                catalogueItemStatus ===
                                  CatalogueItemConstants.OutOfStock && (
                                  <>
                                    <ClinSpacer height={3} />
                                    <ClinTextInput
                                      name="quantity"
                                      id="quantity"
                                      label={t(
                                        'product_variants:enter_quantity'
                                      )}
                                      type="number"
                                      autoFocus={!hasCurrencyError}
                                      disabled={true}
                                      width={'100%'}
                                    />
                                    <ClinSpacer height={ClinTheme.space[5]} />
                                    <ClinButton
                                      variant="primary"
                                      display="block"
                                      disabled={true}
                                    >
                                      {CatalogueItemConstants.OutOfStock}
                                    </ClinButton>
                                  </>
                                )}

                              {catalogueItem &&
                                catalogueItem.showDistributorInfo &&
                                distributor && (
                                  <>
                                    <ClinText
                                      as="div"
                                      fontSize={ClinTheme.fontSizes[3]}
                                      fontWeight={ClinTheme.fontWeights.medium}
                                      lineHeight={ClinTheme.lineHeights.body}
                                      marginTop={ClinTheme.space[4]}
                                    >
                                      {t(
                                        'product_detail:affiliate_availability'
                                      )}
                                    </ClinText>
                                    <ClinSpacer />
                                    <ClinText
                                      variant={TypographyVariant.Paragraph}
                                    >
                                      {t('product_detail:please_contact_them')}
                                    </ClinText>
                                    <ClinSpacer hasBorder />
                                    {distributor.distributorName && (
                                      <ClinText
                                        marginTop={ClinTheme.space[3]}
                                        variant={TypographyVariant.Paragraph}
                                      >
                                        <strong>
                                          {distributor.distributorName}
                                        </strong>
                                      </ClinText>
                                    )}
                                    {distributor.phoneNumber && (
                                      <ClinText
                                        as="div"
                                        variant={TypographyVariant.Paragraph}
                                        marginBottom={ClinTheme.space[2]}
                                      >
                                        {distributor.phoneNumber
                                          .split(',')
                                          .map((ph, i) => (
                                            <div key={`phone-${i}`}>{ph}</div>
                                          ))}
                                      </ClinText>
                                    )}
                                    {distributor.emailAddress && (
                                      <ClinText
                                        as="div"
                                        wordBreak="break-word"
                                        variant={TypographyVariant.Paragraph}
                                        marginBottom={ClinTheme.space[2]}
                                      >
                                        {distributor.emailAddress
                                          .split(',')
                                          .map((ph, i) => (
                                            <div key={`email-${i}`}>{ph}</div>
                                          ))}
                                      </ClinText>
                                    )}
                                    {distributor.website && (
                                      <ClinText
                                        variant={TypographyVariant.Paragraph}
                                        wordBreak="break-word"
                                      >
                                        {distributor.website}
                                      </ClinText>
                                    )}
                                    {distributor.address && (
                                      <ClinText
                                        as="div"
                                        variant={TypographyVariant.Paragraph}
                                      >
                                        {distributor.address
                                          .split(',')
                                          .map((ph, i) => (
                                            <div key={`address-${i}`}>{ph}</div>
                                          ))}
                                      </ClinText>
                                    )}
                                    {/*<pre>{JSON.stringify(distributorInfo, null, 2)}</pre>*/}
                                  </>
                                )}

                              {/* SKU selector pricing and quantity selector panel */}
                              {catalogueItem && !catalogueItem.cannotOrder && (
                                <div>
                                  <div className={'price-row'}>
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        width: '100%'
                                      }}
                                    >
                                      <ClinText
                                        className={'price-title'}
                                        as={'span'}
                                        fontSize={ClinTheme.fontSizes[0]}
                                        fontWeight={
                                          ClinTheme.fontWeights.normal
                                        }
                                      >
                                        {t('product_detail:pack_price')}
                                      </ClinText>
                                      <ClinText
                                        as="span"
                                        className={'unit-price'}
                                        fontSize={ClinTheme.fontSizes[0]}
                                        fontWeight={
                                          ClinTheme.fontWeights.medium
                                        }
                                      >
                                        {Boolean(unitPrice) &&
                                          Boolean(currencyCode) &&
                                          numberToCurrencyString(
                                            unitPrice,
                                            getBrowserLocale(),
                                            {
                                              style: 'currency',
                                              currency: currencyCode,
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2
                                            }
                                          )}
                                      </ClinText>
                                    </div>
                                  </div>
                                  <ClinSpacer
                                    hasBorder={true}
                                    height={ClinTheme.space[7]}
                                  />
                                  {!isAusMaUser(userCountry, userRole) ? ( //related to clos-1614 we don't want to show this part for aus ma users
                                    <div>
                                      <ClinTextInput
                                        name="quantity"
                                        id="quantity"
                                        label={t(
                                          'product_variants:enter_quantity'
                                        )}
                                        labelFontSize={ClinTheme.fontSizes[1]}
                                        labelFontWeight={
                                          ClinTheme.fontWeights.normal
                                        }
                                        type="number"
                                        autoFocus={!hasCurrencyError}
                                        hasError={!!quantityError}
                                        prompt={
                                          quantityError ? quantityError : ''
                                        }
                                        step={stepSize || undefined}
                                        min={minQuantity || undefined}
                                        max={maxQuantity || 10000}
                                        value={quantity}
                                        width={'100%'}
                                        onChange={(event) =>
                                          handleChangeQuantity &&
                                          handleChangeQuantity(
                                            Number.parseInt(
                                              event.currentTarget.value
                                            )
                                          )
                                        }
                                      />

                                      <div className={'price-row'}>
                                        <ClinText
                                          as="div"
                                          variant={TypographyVariant.Paragraph}
                                          fontWeight={
                                            ClinTheme.fontWeights.medium
                                          }
                                          lineHeight={
                                            ClinTheme.lineHeights.body
                                          }
                                          marginTop={ClinTheme.space[3]}
                                          marginBottom={ClinTheme.space[3]}
                                        >
                                          {t('product_detail:subtotal')}
                                        </ClinText>
                                        <ClinText
                                          as="span"
                                          className={'total-cost'}
                                          fontSize={ClinTheme.fontSizes[2]}
                                          fontWeight={
                                            ClinTheme.fontWeights.medium
                                          }
                                          lineHeight={
                                            ClinTheme.lineHeights.body
                                          }
                                        >
                                          {currencyCode &&
                                            numberToCurrencyString(
                                              totalCost,
                                              getBrowserLocale(),
                                              {
                                                style: 'currency',
                                                currency: currencyCode,
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                              }
                                            )}
                                        </ClinText>
                                      </div>
                                      <ClinSpacer height={ClinTheme.space[3]} />
                                      <ClinButton
                                        variant={'primary'}
                                        display={'inline-flex'}
                                        style={{ width: '100%' }}
                                        disabled={
                                          quantity === 0 ||
                                          isLoadingPrice ||
                                          !!quantityError ||
                                          isAddingToBasket ||
                                          isDrawerLoading ||
                                          hasCurrencyError
                                        }
                                        onClick={handleAddToBasket}
                                      >
                                        {(isLoadingPrice ||
                                          isDrawerLoading) && (
                                          <ClinSpinner
                                            size={ClinTheme.space[3]}
                                          />
                                        )}
                                        <StyledButtonLabel>
                                          {t('product_detail:add_to_basket')}
                                        </StyledButtonLabel>
                                      </ClinButton>
                                    </div>
                                  ) : (
                                    <ProductDetailContact
                                      itemSku={catalogueItem.sku}
                                    />
                                  )}
                                </div>
                              )}
                            </StyledSKUQuantitySelector>
                          </>
                          <ClinSpacer />
                        </Col>
                      )}
                    </StyledRowMobile>
                  )}
                  {/*<pre>{JSON.stringify(catalogueItem, null, 2)}</pre>*/}
                </Col>
              </Row>
            </>
          )}
        </StyledProductDetail>
      </ClinPageContentFrame>
    </>
  )
}
